'use client';

import React from 'react';
import { ConceptCardComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';

interface ConceptCardProps {
  component: ConceptCardComponent;
}

export const ConceptCard: React.FC<ConceptCardProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);

  const getVariantStyles = () => {
    switch (props.variant) {
      case 'primary':
        // Muted Sage - calm, natural learning
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-200', accent: 'border-learn-sage-400', title: 'text-learn-sage-900' };
      case 'success':
        // Growth Green - progress (softer green)
        return { bg: 'bg-green-50', border: 'border-green-200', accent: 'border-green-400', title: 'text-green-900' };
      case 'warning':
        // Muted Honey/Amber - gentle attention
        return { bg: 'bg-learn-honey-50', border: 'border-learn-honey-200', accent: 'border-learn-honey-400', title: 'text-learn-honey-900' };
      case 'danger':
        return { bg: 'bg-red-50', border: 'border-red-200', accent: 'border-red-400', title: 'text-red-900' };
      case 'info':
        // Muted Sky Blue - calm information
        return { bg: 'bg-learn-sky-50', border: 'border-learn-sky-200', accent: 'border-learn-sky-400', title: 'text-learn-sky-900' };
      case 'highlight':
        // Muted Clay/Coral - key concepts
        return { bg: 'bg-learn-coral-50', border: 'border-learn-coral-200', accent: 'border-learn-clay-400', title: 'text-learn-clay-900' };
      default:
        // Very soft neutral
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-100', accent: 'border-learn-sage-300', title: 'text-gray-900' };
    }
  };

  const styles = getVariantStyles();
  
  const getBorderClass = () => {
    if (props.borderAccent === 'left') return `border-l-4 ${styles.accent}`;
    if (props.borderAccent === 'top') return `border-t-4 ${styles.accent}`;
    return '';
  };

  const getElevationClass = () => {
    switch (props.elevation) {
      case 'flat':
        return '';
      case 'raised':
        return 'shadow-sm hover:shadow-md';
      case 'floating':
        return 'shadow-lg hover:shadow-xl';
      default:
        return 'shadow-sm';
    }
  };

  const animationClass = isVisible
    ? props.animation === 'fade-in'
      ? 'animate-fadeIn'
      : props.animation === 'slide-up'
      ? 'animate-slideUp'
      : ''
    : '';

  return (
    <div
      ref={ref}
      className={`w-full ${styles.bg} border ${styles.border} ${getBorderClass()} rounded-lg p-5 ${getElevationClass()} transition-all duration-200 ${animationClass}`}
      style={{ opacity: isVisible ? undefined : 0 }}
    >
      {/* Header with Icon and Title */}
      {(props.title || props.icon) && (
        <div className="flex items-start gap-3 mb-3">
          {props.icon && (
            <div className="flex-shrink-0 mt-1">
              <ComponentIcon icon={props.icon} className={`w-5 h-5 ${styles.title}`} />
            </div>
          )}
          {props.title && (
            <h4 className={`text-lg font-bold ${styles.title} flex-1`}>
              {props.title}
            </h4>
          )}
        </div>
      )}

      {/* Main Content */}
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
        {props.content}
      </p>

      {/* Footer */}
      {props.footer && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 italic">
            {props.footer}
          </p>
        </div>
      )}
    </div>
  );
};

